import {
  traitFiltersDecode,
  traitFiltersEncode,
} from "@glow/react/components/nfts/NftTraitFilters";
import { GlowGetApi, NftTypes, range, Solana } from "@glow/shared";
import { LuxButton } from "@lux/atoms/components/LuxButton";
import { LuxLink } from "@lux/atoms/components/LuxLink";
import { LuxMenu } from "@lux/atoms/components/LuxMenu";
import { useBoolean } from "@lux/atoms/hooks/useBoolean";
import HashIcon from "@lux/icons/feather/hash.svg";
import StarIcon from "@lux/icons/feather/star.svg";
import classNames from "classnames";
import { useRouter } from "next/router";
import React, { useEffect, useState } from "react";

import { useZmClientInfinite } from "@components/hooks/useZmClientInfinite";
import { ResponsiveBreakpoint } from "@utils/StyleConstants";
import { addArgToUrl, removeArgFromUrl } from "@utils/url";
import { CollectionFilters } from "./CollectionFilter";
import { useCollectionPageContext } from "./CollectionPageContext";
import { CollectionTabBar } from "./CollectionTabBar";
import { NFTCard, NFTLoadingCard } from "./NFTCard";

type SortMethod = "item-id" | "rarity";

const SortMethodToInfo: Record<
  SortMethod,
  {
    params: Partial<
      GlowGetApi["/solana/explore/get-nfts-for-collection"]["request"]
    >;
    label: string;
    icon: React.ReactNode;
  }
> = {
  rarity: {
    params: {
      sort_column: "nft_rarity_score",
      sort_direction: "desc",
    },
    label: "Rarity",
    icon: <StarIcon />,
  },
  "item-id": {
    params: {
      sort_column: "item_id_decimal",
      sort_direction: "asc",
    },
    label: "Name",
    icon: <HashIcon />,
  },
};

const SortMethods: SortMethod[] = ["item-id", "rarity"];

export const CollectionNftGrid = () => {
  const { data, emptyCollectionFilters } = useCollectionPageContext();
  const collection = data.collection;
  const router = useRouter();
  const [traitFilters, setTraitFilters] = useState<NftTypes.NftTrait[]>(
    traitFiltersDecode(router.query),
  );
  const [sortMethod, setSortMethod] = useState<SortMethod>("item-id");

  const {
    data: nfts,
    hasMore,
    loadMore,
    resetNumPages,
  } = useZmClientInfinite({
    path: "/solana/explore/get-nfts-for-collection",
    args: {
      nft_collection_api_id: collection.api_id,
      trait_filters: traitFilters,
      network: collection.network,
      ...SortMethodToInfo[sortMethod].params,
    },
    pageSize: 24,
  });

  useEffect(() => {
    resetNumPages();
    if (traitFilters?.length > 0) {
      addArgToUrl({
        trait: traitFiltersEncode(traitFilters),
      });
    } else {
      removeArgFromUrl("trait");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [traitFilters, resetNumPages]);
  const filterOpen = useBoolean();

  return (
    <div>
      <CollectionTabBar>
        {data.collection.network === Solana.Network.Mainnet && (
          <LuxMenu
            trigger={
              <LuxButton
                label={`Sort by ${SortMethodToInfo[sortMethod].label}`}
                iconPlacement={"right"}
                color={"light"}
                rounded
                className="filter-button"
                size="small"
              />
            }
            open={filterOpen.value}
            setOpen={filterOpen.setValue}
            placement={"bottom-end"}
            rows={SortMethods.map((sm) => {
              const info = SortMethodToInfo[sm];
              return {
                key: sm,
                name: info.label,
                icon: info.icon,
                onClick: () => {
                  setSortMethod(sm);
                },
              };
            })}
          />
        )}
      </CollectionTabBar>

      <div
        className={classNames("nft-section-wrapper flex-start", {
          "empty-filters": emptyCollectionFilters,
        })}
      >
        {!emptyCollectionFilters && (
          <div className="filters-wrapper">
            <CollectionFilters
              traitsSummary={collection.traits_summary}
              traitFilters={traitFilters}
              setTraitFilters={setTraitFilters}
            />
          </div>
        )}

        <div className="nft-wrapper flex-1">
          <div className="nfts">
            {nfts?.length > 0 &&
              nfts.map((nft) => (
                <LuxLink
                  key={nft.api_id}
                  query={{ network: nft.network }}
                  href={`/token/${nft.mint_address}`}
                >
                  <NFTCard info={nft} />
                </LuxLink>
              ))}

            {nfts?.length === 0 && <div>No Results</div>}

            {(!nfts || hasMore) &&
              range(12).map((i) => (
                <NFTLoadingCard
                  // We have to offset the key by nfts.length so that loading more NFTs changes the key
                  key={(nfts?.length ?? 0) + i}
                  loadMore={i == 0 && hasMore ? loadMore : undefined}
                />
              ))}
          </div>
        </div>
      </div>

      <style jsx>{`
        .nft-section-wrapper {
          @media (max-width: ${ResponsiveBreakpoint.medium}) {
            display: block;
          }

          &.empty-filters {
            .nfts {
              @media (max-width: ${ResponsiveBreakpoint.medium}) {
                padding-top: 1rem;
              }
            }
          }
        }

        .filters-wrapper {
          width: var(--sidebar-width);
          margin-right: 2rem;
          position: sticky;
          top: var(--nav-height);
          z-index: 2;
          max-height: calc(100vh - var(--nav-height) - 1.5rem);
          overflow: auto;
          padding-top: 2rem;

          :global(.luma-button) {
            box-shadow: none !important;
          }

          @media (max-width: ${ResponsiveBreakpoint.communityLargeTwoCol}) {
            padding-top: 1.5rem;
          }

          @media (max-width: ${ResponsiveBreakpoint.medium}) {
            width: 100vw;
            margin: 0 calc(-1 * var(--side-padding));
            padding: 0;
            top: var(--nav-height);
            max-height: none;
            overflow: initial;
            padding-top: 0;
          }
        }

        .nfts {
          display: grid;
          gap: 2rem;
          grid-template-columns: 1fr 1fr 1fr 1fr;
          margin-top: 2rem;

          @media (max-width: ${ResponsiveBreakpoint.communityLargeTwoCol}) {
            margin-top: 1.5rem;
            grid-template-columns: 1fr 1fr 1fr;
            gap: 1.5rem;
          }

          @media (max-width: 900px) {
            grid-template-columns: 1fr 1fr;
          }

          @media (max-width: ${ResponsiveBreakpoint.medium}) {
            grid-template-columns: 1fr 1fr 1fr;
            margin-top: 0;
          }

          @media (max-width: ${ResponsiveBreakpoint.small}) {
            grid-template-columns: 1fr 1fr;
          }
        }
      `}</style>
    </div>
  );
};
